import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/blog/shopify-rechtssicher-deutschland";
const TITLE = "Shopify rechtssicher einrichten: Pflichtangaben für deutsche Shops";
const DESCRIPTION =
  "Impressum, Widerruf, Versandkosten, Grundpreise und Cookie-Einwilligung: welche Angaben ein deutscher Shopify-Shop braucht und wo sie im Theme hinterlegt werden.";
const UPDATED = "2026-09-10";

const ANSWER =
  "Ein deutscher Shopify-Shop braucht Impressum, Datenschutzerklärung, Widerrufsbelehrung mit Muster-Widerrufsformular, AGB, Preisangaben inklusive Umsatzsteuerhinweis und Versandkosten sowie Grundpreise, wo sie vorgeschrieben sind. Tracking-Skripte dürfen erst nach Einwilligung laden. Die Texte selbst gehören zu einer Rechtsberatung, die technische Einbindung ins Theme ist Umsetzungsarbeit.";

const faqs = [
  {
    q: "Welche Pflichtseiten braucht ein deutscher Shopify-Shop?",
    a: "Impressum, Datenschutzerklärung, Widerrufsbelehrung samt Muster-Widerrufsformular und AGB. Diese Seiten müssen aus jeder Ansicht erreichbar sein, üblicherweise über die Fußzeile, und im Bestellprozess verlinkt sein.",
  },
  {
    q: "Wo müssen Versandkosten stehen?",
    a: "Die Angabe zu Versandkosten und Lieferzeit gehört sichtbar an den Preis auf der Produktseite und muss vor Abschluss der Bestellung eindeutig sein. Ein Link zu einer Versandseite genügt in der Regel, wenn er direkt beim Preis steht.",
  },
  {
    q: "Wann darf Tracking im Shop laden?",
    a: "Erst nach aktiver Einwilligung. Analyse- und Marketing-Skripte müssen bis dahin blockiert bleiben, technisch notwendige Funktionen dürfen laufen. Shopify bietet dafür eine Einwilligungssteuerung, die das Theme respektieren muss.",
  },
  {
    q: "Ersetzt dieser Beitrag eine Rechtsberatung?",
    a: "Nein. Der Beitrag beschreibt, welche Angaben üblicherweise verlangt werden und wo sie technisch im Shop hinterlegt werden. Die Rechtstexte selbst sollten von einer Rechtsberatung oder einem spezialisierten Anbieter kommen.",
  },
];

export const Route = createFileRoute("/blog/shopify-rechtssicher-deutschland")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify Aufbau",
      updated: UPDATED,
      breadcrumb: { name: "Blog", path: PATH },
    }),
  }),
  component: Page,
});

function Page() {
  return (
    <ArticlePage
      kicker="Blog · Shopaufbau"
      title="Shopify rechtssicher einrichten"
      intro="Die Pflichtangaben sind kein Randthema: Sie stehen genau dort, wo Kunden ohnehin nach Sicherheit suchen. Gut eingebunden schaffen sie Vertrauen, schlecht eingebunden kosten sie Bestellungen. Dieser Beitrag ordnet die üblichen Anforderungen und zeigt, wo sie im Shop hinterlegt werden."
      source="blog-shopify-rechtssicher"
      answer={ANSWER}
      updated={UPDATED}
      breadcrumb={{ to: "/blog", label: "Blog" }}
      sections={[
        {
          h: "Die Pflichtseiten",
          p: [
            "Impressum, Datenschutzerklärung, Widerrufsbelehrung mit Muster-Widerrufsformular und AGB gehören in jede Fußzeile und in den Bestellprozess. In Shopify werden sie als Seiten angelegt und im Menü der Fußzeile verlinkt, die Checkout-Verlinkung erfolgt in den Richtlinien-Einstellungen.",
            "Die Texte selbst sollten aus einer Rechtsberatung oder von einem spezialisierten Anbieter kommen. Mustertexte aus dem Netz passen häufig nicht zum Sortiment, etwa bei digitalen Produkten oder Abonnements.",
          ],
        },
        {
          h: "Preise, Versand und Grundpreise",
          p: [
            "Bei Preisen muss erkennbar sein, dass die Umsatzsteuer enthalten ist und welche Versandkosten anfallen. Beides gehört direkt an den Preis auf der Produktseite, nicht nur in eine separate Unterseite.",
            "Für viele Waren gilt zusätzlich die Grundpreisangabe, etwa je Kilogramm oder Liter. In Shopify lässt sich das über Maßangaben am Produkt und eine Theme-Anpassung sauber ausgeben, statt es in die Beschreibung zu schreiben.",
          ],
          list: [
            "Hinweis zur enthaltenen Umsatzsteuer am Preis",
            "Versandkosten und Lieferzeit sichtbar auf der Produktseite",
            "Grundpreis, wo vorgeschrieben",
            "Zahlungsarten vor dem Checkout erkennbar",
          ],
        },
        {
          h: "Einwilligung und Tracking",
          p: [
            "Analyse- und Marketing-Skripte dürfen erst nach aktiver Einwilligung laden. Shopify stellt dafür eine Einwilligungssteuerung bereit, an die sich Theme und Apps halten müssen. In der Praxis prüfe ich das, weil ältere App-Einbindungen die Steuerung häufig umgehen.",
            "Ein sauber eingebundener Einwilligungsdialog kostet nicht zwingend Datenqualität. Wichtiger als jedes Detail im Dialog ist, dass er schnell lädt, die Seite nicht blockiert und mobil bedienbar bleibt.",
          ],
        },
        {
          h: "Pflichtangaben als Vertrauenselement nutzen",
          p: [
            "Rückgaberecht, Lieferzeit und Kontaktmöglichkeit gehören nicht nur auf Unterseiten, sondern in Kurzform unter den Kaufbutton. Genau dort entstehen die letzten Zweifel, und genau dort beantworten diese Angaben sie.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-shop-erstellen-lassen", label: "Shopify Shop erstellen lassen" },
        { to: "/blog/shopify-checkout-optimieren", label: "Checkout optimieren" },
        { to: "/blog/shopify-zahlungsarten-deutschland", label: "Zahlungsarten in Deutschland" },
      ]}
    />
  );
}
